import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, TextField, Button, useMediaQuery, useTheme } from '@mui/material';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
    Elements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import EditIcon from '../../../assets/icons/EditBlue.svg';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import PaymentApi from '../../../rest-api/payment-api';
import { useSnackbar } from '../SnackbarProvider';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import UserApi from '../../../rest-api/user-api';
import KlaviyoApi from '../../../rest-api/klaviyo-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import getDiscountByPlan from '../../helper/getDiscountHelper';
import mixpanel from 'mixpanel-browser';
import { practiceOrClinicText } from '../../../config';

const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_PUBLIC_KEY}`);

const SettingsBilling = ({
    currentPaymentMethod,
    plan = null,
    editorActiveDefault = false,
    onPromoDiscountChange,
    showPromo,
    isShowOffer,
    isExpo,
    isOnboarding = false,
    showEditAction = true,
    sectionSx = {},
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [editorActive, setEditorActive] = useState(editorActiveDefault);
    const [cardholderName, setCardholderName] = useState('');
    const [billingAddress, setBillingAddress] = useState({
        line1: '',
        line2: '',
        city: '',
        postal_code: '',
        country: 'GB',
    });
    const [planDetails, setPlanDetails] = useState(plan);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const stripe = useStripe();
    const elements = useElements();
    const { authStore, userDataStore } = useContext(StoreContext);
    const showSnackbar = useSnackbar();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [promoCode, setPromoCode] = useState(); // removed 'LVS' code
    const [promoMessage, setPromoMessage] = useState(null);
    const [promoStatus, setPromoStatus] = useState(null);
    const [promoDiscount, setPromoDiscount] = useState(null); // Can be {type: 'percent', value: X} or {type: 'amount', value: Y, currency: 'gbp'}
    const [practiceDetails, setPracticeDetails] = useState({
        clinicName: userDataStore?.userData?.clinicName || '',
        clinicNumber: userDataStore?.userData?.clinicNumber || '',
        clinicSize: userDataStore?.userData?.clinicSize || '',
    });
    const [practiceNameError, setPracticeNameError] = useState('');

    const [paymentRequest, setPaymentRequest] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const requiresPracticeDetails = Boolean(planDetails?.accountType && planDetails.accountType !== 'Free');
    const practiceLabel = practiceOrClinicText[0].toUpperCase() + practiceOrClinicText.slice(1);
    // New signups reach checkout with pendingPlan set during onboarding; existing users can also reach checkout.
    const isNewSignupCheckout = Boolean(userDataStore?.subscriptionData?.pendingPlan);
    const confirmationMessage = isNewSignupCheckout
        ? 'Please confirm that you want to continue.'
        : 'Please confirm that you want to proceed with your upgrade.';
    const confirmationConfirmText = isNewSignupCheckout ? 'Yes, continue' : 'Yes, upgrade';
    const getPendingPlanAccountType = () => {
        const pendingPlan = userDataStore?.subscriptionData?.pendingPlan;
        return typeof pendingPlan === 'string' ? pendingPlan : pendingPlan?.accountType;
    };

    const stripeElementStyle = {
        mb: 2,
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: isOnboarding ? '#fff' : '#F9F9F9',
            '& fieldset': isOnboarding ? { borderColor: '#e5e5e5' } : { border: 'none' },
        },
        '& .MuiOutlinedInput-input': {
            padding: '16px',
            border: 0,
            color: 'black',
        },
    };
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (isShowOffer) {
            setPromoCode(''); // removed LVS
            handleValidatePromoCode();
        }
    }, [isShowOffer]);

    useEffect(() => {
        if (stripe && editorActiveDefault) {
            let discountedPrice = planDetails?.price || 0;

            // Calculate discounted price based on discount type
            if (promoDiscount?.type === 'percent') {
                discountedPrice = planDetails?.price - planDetails?.price * (promoDiscount.value / 100);
            } else if (promoDiscount?.type === 'amount') {
                discountedPrice = Math.max(0, planDetails?.price - promoDiscount.value);
            }

            console.warn('discountedPrice: ', discountedPrice);

            const pr = stripe.paymentRequest({
                country: 'GB',
                currency: 'gbp',
                total: {
                    label: 'Total',
                    amount: Math.round(discountedPrice) || 10,
                },
                requestPayerName: true,
                requestPayerEmail: true,
            });

            console.warn('pr start', pr);

            pr.canMakePayment().then((result) => {
                console.warn('pr then', pr);
                if (result) {
                    setPaymentRequest(pr);
                }
            });
        }
    }, [stripe, planDetails, promoDiscount]);

    useEffect(() => {
        const fetchDiscount = async () => {
            if (planDetails?.accountType && authStore?.idToken) {
                const discount = await getDiscountByPlan(authStore.idToken, planDetails.accountType);
                console.warn('Fetched Discount for:', planDetails.accountType, 'is', discount);

                // getDiscountByPlan returns a percentage number, so wrap it in our new format
                const discountObj = discount ? { type: 'percent', value: discount } : null;
                setPromoDiscount(discountObj);
                onPromoDiscountChange?.(discountObj);
            }
        };

        if (planDetails) {
            fetchDiscount();
        }
    }, [planDetails]);

    const fetchPaymentMethod = async () => {
        try {
            const stripeCustomerId = userDataStore?.subscriptionData?.stripeCustomerId;
            if (stripeCustomerId) {
                const paymentDetails = await PaymentApi.getPaymentDetails(authStore.idToken, stripeCustomerId);
                console.warn('Customer payment details: ', paymentDetails);
                setPaymentDetails(paymentDetails);
            }
        } catch (error) {
            console.error('Error fetching payment method:', error);
        }
    };

    useEffect(() => {
        if (userDataStore && authStore) {
            fetchPaymentMethod();
        }
    }, [userDataStore, authStore]);

    useEffect(() => {
        setPracticeDetails({
            clinicName: userDataStore?.userData?.clinicName || '',
            clinicNumber: userDataStore?.userData?.clinicNumber || '',
            clinicSize: userDataStore?.userData?.clinicSize || '',
        });
    }, [
        userDataStore?.userData?.clinicName,
        userDataStore?.userData?.clinicNumber,
        userDataStore?.userData?.clinicSize,
    ]);

    useEffect(() => {
        if (plan) {
            setPlanDetails(plan);
        }
    }, [plan]);
    // TODO: this should be a helper as well, to use from many places, not only set up in setting-billing
    const handleValidatePromoCode = async () => {
        console.warn(`Applying promo code: ${promoCode}`);
        setPromoMessage(null);
        setPromoStatus(null);
        setPromoDiscount(null);
        const req = {
            promoCode: promoCode,
        };

        try {
            const validatePromoResponse = await PaymentApi.validatePromo(authStore.idToken, req);
            console.warn('Promo validation response:', validatePromoResponse);

            // Check if statusCode is 200 for success
            if (validatePromoResponse.statusCode === 200) {
                setPromoMessage('Promo code successfully applied!');
                setPromoStatus('success');

                const coupon = validatePromoResponse.promotionCode?.coupon;

                // Handle both percentage and fixed amount discounts
                let discount;
                if (coupon?.percent_off) {
                    // Percentage discount (e.g., 5% = 5)
                    discount = { type: 'percent', value: coupon.percent_off };
                } else if (coupon?.amount_off) {
                    // Fixed amount discount in pence, convert to pounds (e.g., 5688 pence = £56.88)
                    discount = { type: 'amount', value: coupon.amount_off / 100, currency: coupon.currency };
                } else {
                    discount = { type: 'none', value: 0 };
                }

                console.warn('Fetched discount:', discount);
                setPromoDiscount(discount);
                onPromoDiscountChange(discount);
            } else {
                setPromoMessage(validatePromoResponse.error || 'Failed to apply promo code.');
                setPromoStatus('error');
            }
        } catch (error) {
            console.error('Error during promo validation:', error);
            setPromoMessage('An error occurred while applying the promo code.');
            setPromoStatus('error');
        }
    };

    const handleBack = async () => {
        navigate(-1);
    };

    const handleConfirmSubscription = async () => {
        console.log('Subscription attempt with', plan);
        await handleSubmitPayment({ preventDefault: () => {} });
    };

    const getPracticeDetailsPayload = () => ({
        clinicName: practiceDetails.clinicName.trim(),
        clinicNumber: practiceDetails.clinicNumber.trim(),
        clinicSize: practiceDetails.clinicSize.trim(),
    });

    const validatePracticeDetails = () => {
        if (!requiresPracticeDetails) {
            setPracticeNameError('');
            return true;
        }

        if (!practiceDetails.clinicName.trim()) {
            setPracticeNameError(`${practiceLabel} name is required`);
            return false;
        }

        setPracticeNameError('');
        return true;
    };

    const handleContinue = () => {
        if (!validatePracticeDetails()) return;
        setDialogOpen(true);
    };

    const buildClinicianUpdatePayload = (subscriptionData) => {
        if (!requiresPracticeDetails) {
            return { subscriptionData };
        }

        return {
            ...getPracticeDetailsPayload(),
            subscriptionData,
        };
    };

    const buildLocalUserDataUpdate = (subscriptionData) => {
        if (!requiresPracticeDetails) {
            return {
                ...userDataStore.getUserData(),
                subscriptionData,
            };
        }

        return {
            ...userDataStore.getUserData(),
            ...getPracticeDetailsPayload(),
            subscriptionData,
        };
    };

    const handlePaymentRequest = async (event) => {
        event.preventDefault();

        if (!stripe || !paymentRequest) {
            return;
        }

        const { error } = await paymentRequest.show();
        if (error) {
            console.error('Error showing payment request:', error);
            showSnackbar('Failed to initiate payment request.', 'error');
        }
    };

    const handleSubmitPayment = async (event) => {
        event.preventDefault();

        if (!validatePracticeDetails()) return;

        setIsProcessing(true); // start spinner

        try {
            // Ensure stripe and elements are available
            if (!stripe || !elements) {
                throw new Error('Stripe has not loaded properly. Please refresh the page.');
            }

            const cardElement = elements.getElement(CardNumberElement);

            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: cardholderName,
                    address: billingAddress,
                    email: userDataStore.userData.emailAddress,
                    phone: userDataStore.userData.mobile,
                },
            });

            // Handle errors during payment method creation
            if (error) {
                console.error('Error creating payment method:', error);
                showSnackbar(error.message || 'An error occurred while processing your payment.', 'error');
                return; // Exit early to avoid sending the payment details to the backend
            }

            // Proceed to send payment details to the backend
            await sendPaymentDetailsToBackend(paymentMethod, plan);
        } catch (err) {
            console.error('Error during payment submission:', err);
            showSnackbar(err.message || 'Something went wrong during the payment process. Please try again.', 'error');
        } finally {
            // Stop spinner does not matter if success or failure
            setIsProcessing(false);
        }
    };

    const getPendingUpgradeHandoutId = () => {
        const getPendingId = (key) => {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            try {
                const data = JSON.parse(raw);
                const isRecent = Date.now() - data.timestamp < 3600000;
                if (!isRecent || !data.handoutId) {
                    localStorage.removeItem(key);
                    return null;
                }
                return data.handoutId;
            } catch (error) {
                localStorage.removeItem(key);
                return null;
            }
        };

        const pendingFromStorage =
            getPendingId('pendingCombinedHandoutSave') ||
            getPendingId('pendingCustomisedHandoutSave') ||
            getPendingId('pendingWhiteboardSave');

        if (pendingFromStorage) {
            return pendingFromStorage;
        }

        const pendingUpgradeHandoutId = sessionStorage.getItem('pendingUpgradeHandoutId');
        const pendingUpgradeSetAt = Number(sessionStorage.getItem('pendingUpgradeHandoutIdSetAt') || 0);
        const isRecent =
            pendingUpgradeHandoutId && pendingUpgradeSetAt > 0 && Date.now() - pendingUpgradeSetAt < 300000;

        if (!isRecent && pendingUpgradeHandoutId) {
            sessionStorage.removeItem('pendingUpgradeHandoutId');
            sessionStorage.removeItem('pendingUpgradeHandoutIdSetAt');
            return null;
        }

        return pendingUpgradeHandoutId;
    };

    const sendPaymentDetailsToBackend = async (paymentMethod, plan) => {
        // Capture before update — if set, this is a new signup (not an upgrade)
        const isPendingNewPlan = getPendingPlanAccountType();

        try {
            const req = {
                userId: userDataStore.userId,
                paymentMethodId: paymentMethod.id,
                email: userDataStore.userData.emailAddress,
                ...(plan && { plan }),
                ...(promoStatus === 'success' && promoCode ? { promoCode } : {}),
                ...(location.state?.resubscribe || new URLSearchParams(location.search).get('resubscribe') === 'true'
                    ? { resubscribe: true }
                    : {}),
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.updatePaymentDetails(bearerToken, req);

            console.warn('Payment details submission response: ', response);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            // Update customerId if returned
            if (response.customerId) {
                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    stripeCustomerId: response.customerId,
                };
            }

            if (response.requiresAction) {
                showSnackbar('Payment pending - please complete the security check with your bank', 'success');
                const result = await stripe.confirmCardPayment(response.paymentIntentClientSecret);
                if (result.error) {
                    showSnackbar('3D Secure authentication failed.', 'error');
                } else if (result.paymentIntent.status === 'succeeded') {
                    await confirmPaymentIntent(result.paymentIntent.id, response.subscriptionId);
                } else if (result.paymentIntent.status === 'requires_confirmation') {
                    await confirmPaymentIntent(result.paymentIntent.id, response.subscriptionId);
                }
            } else if (response.subscription) {
                // Handle subscription success
                const { id, status, items, current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: plan?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null, // Convert seconds to milliseconds
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('sub data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData?.accountType;
                const newPlan = planDetails?.accountType;
                await UserApi.updateClinicianData(
                    authStore.idToken,
                    buildClinicianUpdatePayload(updatedSubscriptionData),
                );
                userDataStore.setUserData(buildLocalUserDataUpdate(updatedSubscriptionData));
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    planDetails?.accountType,
                    isPendingNewPlan ? { preserveExistingLists: true } : undefined,
                );
                if (isPendingNewPlan === 'Professional') {
                    mixpanel.identify(userDataStore.userData.emailAddress);
                    mixpanel.track('ProfessionalPlanCreated', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (isPendingNewPlan === 'Clinic') {
                    mixpanel.identify(userDataStore.userData.emailAddress);
                    mixpanel.track('ClinicPlanCreated', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (previousPlan === 'Free' && newPlan === 'Professional') {
                    mixpanel.track('UpgradeFromFreeToProfessional', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (previousPlan === 'Free' && newPlan === 'Clinic') {
                    mixpanel.track('UpgradeFromFreeToClinic', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                }
                if (response.subscription) {
                    const pendingHandoutId = getPendingUpgradeHandoutId();
                    if (pendingHandoutId) {
                        showSnackbar(
                            'Your subscription details have been successfully updated. Open your saved handout to continue.',
                            'success',
                        );
                        return;
                    }
                    navigate('/', { state: { subscriptionSuccess: true } });
                } else {
                    showSnackbar('Billing details have been successfully updated.', 'success');
                }
            }
        } catch (error) {
            console.error('Error sending payment details to backend:', error);
            showSnackbar('Error when updating billing details, please try again.', 'error');
        }
    };

    const confirmPaymentIntent = async (paymentIntentId, subscriptionId) => {
        console.warn('Check this Payment Intent: ', paymentIntentId);
        const isPendingNewPlan = getPendingPlanAccountType();
        try {
            const req = {
                paymentIntentId,
                userId: userDataStore.userId,
                subscriptionId: subscriptionId,
            };

            const bearerToken = authStore.idToken;

            const response = await PaymentApi.confirmPaymentDetails(bearerToken, req);

            let updatedSubscriptionData = { ...userDataStore.subscriptionData };

            // Update customerId if returned
            if (response.customerId) {
                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    stripeCustomerId: response.customerId,
                };
            }

            if (response.subscription) {
                const { id, status, items, current_period_end } = response.subscription;

                updatedSubscriptionData = {
                    ...updatedSubscriptionData,
                    accountType: planDetails?.accountType || null,
                    trialActive: false,
                    trialEndDate: null, // Clear trial data when converting to paid plan
                    subscriptionEndDate: current_period_end * 1000 || null,
                    cancelled: false,
                    pendingPlan: null,
                    stripeCustomerId: response.customerId || updatedSubscriptionData.stripeCustomerId,
                };

                console.warn('subscription data post stripe: ', updatedSubscriptionData);
            }

            // Update user data if changes occurred
            if (response.customerId || response.subscription) {
                const previousPlan = userDataStore.subscriptionData?.accountType;
                const newPlan = planDetails?.accountType;
                await UserApi.updateClinicianData(
                    authStore.idToken,
                    buildClinicianUpdatePayload(updatedSubscriptionData),
                );
                userDataStore.setUserData(buildLocalUserDataUpdate(updatedSubscriptionData));
                authStore.checkAndSetAuthState(userDataStore.userData.emailAddress, authStore.idToken);
                await KlaviyoApi.updateUserInKlaviyo(
                    authStore.idToken,
                    userDataStore.userData.emailAddress,
                    planDetails?.accountType,
                    isPendingNewPlan ? { preserveExistingLists: true } : undefined,
                );
                if (isPendingNewPlan === 'Professional') {
                    mixpanel.identify(userDataStore.userData.emailAddress);
                    mixpanel.track('ProfessionalPlanCreated', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (isPendingNewPlan === 'Clinic') {
                    mixpanel.identify(userDataStore.userData.emailAddress);
                    mixpanel.track('ClinicPlanCreated', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (previousPlan === 'Free' && newPlan === 'Professional') {
                    mixpanel.track('UpgradeFromFreeToProfessional', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                } else if (previousPlan === 'Free' && newPlan === 'Clinic') {
                    mixpanel.track('UpgradeFromFreeToClinic', {
                        userId: userDataStore.userId,
                        email: userDataStore.userData.emailAddress,
                    });
                }
                if (response.subscription) {
                    const pendingHandoutId = getPendingUpgradeHandoutId();
                    if (pendingHandoutId) {
                        showSnackbar(
                            'Your subscription details have been successfully updated. Open your saved handout to continue.',
                            'success',
                        );
                        return;
                    }
                    navigate('/', { state: { subscriptionSuccess: true } });
                }
                // Re-fetch payment methods to update state
                await fetchPaymentMethod();
            }
        } catch (error) {
            console.error('Error confirming payment intent:', error);
            showSnackbar('Error when confirming payment.', 'error');
        }
    };

    const renderStripeField = () => {
        const { card, billing_details } = paymentDetails?.paymentMethods?.[0] || {};

        return (
            <Box key="cardDetails" sx={{ position: 'relative' }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Card number
                    </Typography>
                    {editorActive ? (
                        <Box
                            sx={{
                                borderRadius: '12px',
                                padding: '16px',
                                backgroundColor: isOnboarding ? '#fff' : '#f9f9f9',
                                border: isOnboarding ? '1px solid #e5e5e5' : 'none',
                                height: '55px',
                            }}
                        >
                            <CardNumberElement />
                        </Box>
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                            **** **** **** {card?.last4 || '****'}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Name on card
                    </Typography>
                    {editorActive ? (
                        <TextField
                            variant="outlined"
                            fullWidth
                            value={cardholderName}
                            onChange={(e) => setCardholderName(e.target.value)}
                            placeholder="Cardholder name"
                            sx={stripeElementStyle}
                        />
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                            {billing_details?.name || '********'}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', mb: 2 }}>
                    <Box sx={{ flex: 1, mr: isMobile ? 0 : 2, mb: isMobile ? 3 : 0 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Expiration date
                        </Typography>
                        {editorActive ? (
                            <Box
                                sx={{
                                    borderRadius: '12px',
                                    padding: '16px',
                                    backgroundColor: isOnboarding ? '#fff' : '#f9f9f9',
                                    border: isOnboarding ? '1px solid #e5e5e5' : 'none',
                                    maxWidth: '100%',
                                    width: '100%',
                                }}
                            >
                                <CardExpiryElement />
                            </Box>
                        ) : (
                            <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                                {card ? `${card.exp_month}/${card.exp_year}` : '**/**'}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            CVC
                        </Typography>
                        {editorActive ? (
                            <Box
                                sx={{
                                    borderRadius: '12px',
                                    padding: '16px',
                                    backgroundColor: isOnboarding ? '#fff' : '#f9f9f9',
                                    border: isOnboarding ? '1px solid #e5e5e5' : 'none',
                                    maxWidth: '100%',
                                    width: '100%',
                                }}
                            >
                                <CardCvcElement />
                            </Box>
                        ) : (
                            <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                                ***
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Billing address
                    </Typography>
                    {editorActive ? (
                        <>
                            <TextField
                                label="Address line 1"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.line1}
                                onChange={(e) => setBillingAddress({ ...billingAddress, line1: e.target.value })}
                                sx={stripeElementStyle}
                            />
                            <TextField
                                label="Address line 2"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.line2}
                                onChange={(e) => setBillingAddress({ ...billingAddress, line2: e.target.value })}
                                sx={stripeElementStyle}
                            />
                            <TextField
                                label="City"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.city}
                                onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                                sx={stripeElementStyle}
                            />
                            <TextField
                                label="Postal code"
                                variant="outlined"
                                fullWidth
                                value={billingAddress.postal_code}
                                onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
                                sx={stripeElementStyle}
                            />
                        </>
                    ) : (
                        <Typography variant="body1" sx={{ fontSize: '16px', color: '#32325d' }}>
                            {billing_details?.address?.line1 ? `${billing_details.address.line1}, ` : '********** '}
                            {billing_details?.address?.line2 ? `${billing_details.address.line2}, ` : ''}
                            {billing_details?.address?.city ? `${billing_details.address.city}, ` : '***** '}
                            {billing_details?.address?.postal_code
                                ? `${billing_details.address.postal_code} `
                                : '***** '}
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    };

    const renderPracticeDetailsSection = () => {
        if (!requiresPracticeDetails) return null;

        const practiceFieldSx = {
            '& .MuiOutlinedInput-root': {
                height: '56px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                '& fieldset': {
                    borderColor: 'rgba(102, 102, 102, 0.35)',
                },
                '&:hover fieldset': {
                    borderColor: 'rgba(102, 102, 102, 0.35)',
                },
                '&.Mui-focused fieldset': {
                    borderColor: 'rgba(102, 102, 102, 0.35)',
                    borderWidth: '1px',
                },
            },
            '& .MuiOutlinedInput-input': {
                padding: '16px',
            },
        };

        const renderPracticeField = (label, key, extraProps = {}) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                <Typography
                    sx={{
                        color: '#616063',
                        fontSize: { xs: '14px', md: '16px' },
                        fontWeight: 400,
                        lineHeight: '27px',
                        letterSpacing: { xs: '-0.28px', md: '-0.32px' },
                    }}
                >
                    {label}
                </Typography>
                <TextField
                    variant="outlined"
                    fullWidth
                    value={practiceDetails[key]}
                    onChange={(e) => {
                        const value = key === 'clinicNumber' ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
                        setPracticeDetails((prevDetails) => ({ ...prevDetails, [key]: value }));
                        if (key === 'clinicName' && value.trim()) {
                            setPracticeNameError('');
                        }
                    }}
                    sx={practiceFieldSx}
                    {...extraProps}
                />
            </Box>
        );

        return (
            <Box
                className="settings-block"
                sx={{
                    backgroundColor: 'white',
                    borderRadius: '30px',
                    border: '1px solid rgba(102, 102, 102, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: { xs: '24px', md: '40px 56px' },
                    mt: 3,
                    mb: 0,
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        color: '#000453',
                        fontWeight: 600,
                        fontSize: { xs: '26px', md: '36px' },
                        lineHeight: { xs: '26px', md: '36px' },
                        letterSpacing: { xs: '-0.52px', md: '-0.72px' },
                        textAlign: 'center',
                        mb: '40px',
                    }}
                >
                    {practiceLabel} details
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        width: '100%',
                        maxWidth: '534px',
                    }}
                >
                    {renderPracticeField(`${practiceLabel} name`, 'clinicName', {
                        error: Boolean(practiceNameError),
                        helperText: practiceNameError,
                    })}
                    {renderPracticeField(`${practiceLabel} phone number`, 'clinicNumber')}
                    {renderPracticeField(`${practiceLabel} size`, 'clinicSize')}
                </Box>
            </Box>
        );
    };

    return (
        <Box className="settings-container">
            <Box
                className="settings-block"
                sx={{
                    backgroundColor: 'white',
                    borderRadius: '30px',
                    border: '1px solid rgba(102, 102, 102, 0.15)',
                    padding: isOnboarding ? { xs: '24px', md: '40px 56px' } : { xs: '20px 22px', md: '24px 30px' },
                    my: isOnboarding ? 0 : 3,
                    ...sectionSx,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: isOnboarding ? 'center' : 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: isOnboarding ? 600 : 'bold',
                            color: '#000453',
                            textAlign: isOnboarding ? 'center' : 'left',
                            fontSize: isOnboarding ? { xs: '26px', md: '36px' } : undefined,
                            letterSpacing: isOnboarding ? { xs: '-0.52px', md: '-0.72px' } : undefined,
                            lineHeight: isOnboarding ? { xs: '26px', md: '36px' } : undefined,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        Billing details
                    </Typography>
                    {!editorActiveDefault && showEditAction && (
                        <IconButton
                            onClick={() => setEditorActive(!editorActive)}
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            sx={{
                                p: 0,
                                width: 38,
                                height: 38,
                                borderRadius: '38px',
                                bgcolor: editorActive ? '#000453' : '#FF669B',
                                '&:hover': { bgcolor: editorActive ? '#1C2A8A' : '#FF85B0' },
                                '&:active': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                                '&:focus': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                                '&.Mui-focusVisible': { bgcolor: editorActive ? '#000453' : '#FF669B' },
                                WebkitTapHighlightColor: 'transparent',
                                flexShrink: 0,
                            }}
                        >
                            <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
                        </IconButton>
                    )}
                </Box>

                {/* {paymentRequest && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6">Pay with Google Pay</Typography>
          <Box sx={{ maxWidth: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '200px', height: '60px' }}>
            <PaymentRequestButtonElement
              options={{ paymentRequest }}
              onClick={handlePaymentRequest}
              style={{
                paymentRequestButton: {
                  theme: 'dark', 
                  height: '60px',
                  width: '200px',
                },
              }}
            />
          </Box>
        </Box>
      )} */}

                {renderStripeField()}

                {editorActive && !editorActiveDefault && (
                    <Box display="flex" flexDirection="column" gap={2}>
                        <PinkGradientButton
                            className="black-btn"
                            onClick={handleSubmitPayment}
                            sx={{
                                my: 3,
                                mx: { xs: 'auto', md: 0 },
                                display: 'block',
                                height: '44px',
                                borderRadius: '200px',
                                background: '#FF669B',
                                backgroundImage: 'none',
                                '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                                color: '#FFF',
                            }}
                        >
                            Save changes
                        </PinkGradientButton>
                    </Box>
                )}
            </Box>

            {location.pathname.includes('/checkout') && renderPracticeDetailsSection()}

            {location.pathname.includes('/checkout') && showPromo && (
                <Box
                    className="settings-block"
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: '30px',
                        border: '1px solid rgba(102, 102, 102, 0.15)',
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '40px',
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: { xs: '24px', md: '40px 56px' },
                        mt: 3,
                        mb: 0,
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            color: '#000453',
                            fontWeight: 600,
                            fontSize: { xs: '26px', md: '36px' },
                            lineHeight: { xs: '26px', md: '36px' },
                            letterSpacing: { xs: '-0.52px', md: '-0.72px' },
                            textAlign: 'center',
                        }}
                    >
                        Promo code
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '26px',
                            width: '100%',
                            maxWidth: '534px',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                            <TextField
                                aria-label="Promo code"
                                variant="outlined"
                                fullWidth
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: '56px',
                                        borderRadius: '12px',
                                        backgroundColor: '#fff',
                                        '& fieldset': {
                                            borderColor: 'rgba(102, 102, 102, 0.35)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(102, 102, 102, 0.35)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'rgba(102, 102, 102, 0.35)',
                                            borderWidth: '1px',
                                        },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        padding: '16px',
                                    },
                                }}
                            />
                            {promoMessage && (
                                <Typography
                                    sx={{
                                        color: promoStatus === 'success' ? '#1AC11A' : 'red',
                                        fontSize: { xs: '14px', md: '15px' },
                                        fontWeight: 500,
                                        lineHeight: '27px',
                                        letterSpacing: { xs: '-0.28px', md: '-0.32px' },
                                        whiteSpace: { xs: 'normal', md: 'nowrap' },
                                    }}
                                >
                                    {promoStatus === 'success' ? '✓ ' : ''}
                                    {promoMessage}{' '}
                                    {promoDiscount?.type === 'percent' && promoDiscount.value === 25
                                        ? 'Enjoy 3 months off your plan!'
                                        : promoDiscount?.type === 'percent' && promoDiscount.value
                                          ? `Enjoy a ${promoDiscount.value}% discount on your plan!`
                                          : promoDiscount?.type === 'amount' && promoDiscount.value
                                            ? `Enjoy a £${promoDiscount.value.toFixed(2)} discount on your plan!`
                                            : ''}
                                </Typography>
                            )}
                        </Box>

                        <PinkGradientButton
                            onClick={handleValidatePromoCode}
                            sx={{
                                width: '100%',
                                maxWidth: '534px',
                                height: { xs: '35px', md: '64px' },
                                padding: { xs: 0, md: '18px 0 19px 0' },
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: { xs: '17.437px', md: '32px' },
                                background: '#FF669B',
                                backgroundImage: 'none',
                                color: '#FFF',
                                fontSize: { xs: '14px', md: '22px' },
                                fontWeight: 500,
                                lineHeight: { xs: '14px', md: 'normal' },
                                letterSpacing: { xs: '-0.28px', md: '-0.44px' },
                                '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                            }}
                        >
                            Apply
                        </PinkGradientButton>
                    </Box>
                </Box>
            )}

            {editorActive && editorActiveDefault && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleBack}
                        sx={{
                            mt: '10px',
                            color: '#ff669b',
                            textDecoration: 'underline',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '16px', md: '22px' },
                            letterSpacing: '-0.44px',
                            lineHeight: '22px',
                            p: 0,
                            minWidth: 'auto',
                        }}
                    >
                        ← Go back
                    </Button>
                    <PinkGradientButton
                        onClick={handleContinue}
                        sx={{
                            mt: '18px',
                            width: { xs: '180px', md: '230px' },
                            height: { xs: '38px', md: '64px' },
                            borderRadius: { xs: '18.918px', md: '34.582px' },
                            background: '#FF669B',
                            backgroundImage: 'none',
                            color: '#FFF',
                            fontSize: { xs: '14px', md: '22px' },
                            fontWeight: 500,
                            lineHeight: { xs: '14px', md: 'normal' },
                            letterSpacing: { xs: '-0.28px', md: '-0.44px' },
                            '&:hover': { background: '#FF85B0', backgroundImage: 'none' },
                        }}
                    >
                        Continue
                    </PinkGradientButton>
                </Box>
            )}

            <ConfirmationDialog
                open={dialogOpen}
                handleClose={() => {
                    setDialogOpen(false);
                }}
                onConfirm={handleConfirmSubscription}
                title="Great choice! You're just one click away from more features!"
                message={confirmationMessage}
                confirmText={confirmationConfirmText}
                cancelText="No, cancel"
                actionJustifyContent="flex-end"
            />
            <BlockingSpinner open={isProcessing} />
        </Box>
    );
};

const SettingsBillingWrapper = (props) => {
    return (
        <Elements stripe={stripePromise}>
            <SettingsBilling {...props} />
        </Elements>
    );
};

export default SettingsBillingWrapper;
